import LightMode from '@mui/icons-material/LightMode';
import SwapHoriz from '@mui/icons-material/SwapHoriz';
import Drawer from '@mui/material/Drawer';
import ListItemButton from '@mui/material/ListItemButton';
import { useColorScheme } from '@mui/material/styles';
import { useTheme } from '@mui/material';
import { useContext, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  handleAnalytics,
  navBottomSectionsConfig,
  navTopSectionConfig,
} from './config';
import { usePermissions, UserContext } from '@/providers/userContextProvider';
import { AnalyticsContext } from '@/providers/analyticsProvider';
import { LOG_MESSAGES } from '@/constants/logMessages';
import { logEvent } from '@/util/logger';
import { Box, Icon, NavSection } from '@/components/muiComponents';
import LogoContainer from '@/components/layout/sidebar/logoImageContainer';
import LanguageSelector from '@/components/layout/sidebar/languageSelector';
import Switch from '@/components/muiComponents/basic/switch/Switch';
import { AuthButton } from '@/providers/components/AuthButton';
import { ThemedTooltip } from '@/components/chat/FeedbackContainer';
import {
  isFastApiEnvToggleAvailable,
  setFastApiEnvPref,
  useFastApiEnvPref,
} from '@/util/fastApiUrl';

export const useMenuSections = () => {
  return [...navTopSectionConfig];
};

const Sidebar = (props: any) => {
  const { t } = useTranslation(['navMenu']);
  const { closed, setClosed, mobileOpen, setMobileOpen } =
    useContext(UserContext);
  const { accessiblePages } = usePermissions();
  const [openedMenu] = useState<string>('');
  const { sendAnalytic } = useContext(AnalyticsContext);
  const theme = useTheme();
  const { setMode } = useColorScheme();

  const [manuallyClosed, setManuallyClosed] = useState(false);
  const [hoverTimeout, setHoverTimeout] = useState<NodeJS.Timeout | null>(null);

  const sidebarRef = useRef<HTMLDivElement | null>(null);

  const handleMouseEnter = (e: React.MouseEvent<HTMLDivElement>) => {
    if (closed && !manuallyClosed) {
      const timeout = setTimeout(() => {
        setClosed(false);
        setManuallyClosed(false);
      }, 1000);

      setHoverTimeout(timeout);
    }
  };

  const handleMouseLeave = () => {
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      setHoverTimeout(null);
    }
  };

  const fastApiToggleAvailable = isFastApiEnvToggleAvailable();
  const fastApiEnv = useFastApiEnvPref();

  const toggleFastApiEnv = () => {
    setFastApiEnvPref(fastApiEnv === 'new' ? 'old' : 'new');
  };

  const toggleMode = () => {
    logEvent(LOG_MESSAGES.USER_TOGGLED_DARK_MODE, {
      mode: theme.palette.mode === 'light' ? 'dark' : 'light',
    });
    setMode(theme.palette.mode === 'light' ? 'dark' : 'light');
  };

  const handleDrawerClose = () => {
    logEvent(LOG_MESSAGES.USER_CLOSED_SIDEBAR);
    setMobileOpen(false);
  };

  const handleDrawerClick = (name: string) => {
    logEvent(LOG_MESSAGES.USER_CLICKED_MENU_ITEM, {
      menu: name,
    });
    handleAnalytics(name, sendAnalytic);
    setMobileOpen(false);
  };

  const finalSections = useMenuSections();

  const bottomSections = navBottomSectionsConfig;
  const transition = 'width .5s ease';
  const drawer = (
    <Box
      data-testid="sidebar-drawer"
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
      sx={{
        height: '-webkit-fill-available',
        flexWrap: 'inherit',
      }}
    >
      <LogoContainer
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
        setManuallyClosed={setManuallyClosed}
      />

      <Box
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
        sx={{ flex: 1, overflow: 'hidden' }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {finalSections.slice(0, 1).map((section) => (
          <Box
            key={section.name}
            data-testid={`sidebar-section-${section.name}`}
            sx={{ px: '4px' }}
          >
            <NavSection
              section={section}
              accessiblePages={accessiblePages}
              handleDrawerClick={handleDrawerClick}
              closed={closed && !mobileOpen}
              openedMenu={openedMenu === section.name}
              setMobileOpen={setMobileOpen}
            />
          </Box>
        ))}
        <Box
          key={closed + ''}
          sx={{
            overflowY: 'auto',
            overflowX: 'hidden',
            '&::-webkit-scrollbar': {
              width: '3px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: theme.palette.grey[500],
              borderRadius: '1px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'transparent',
            },
            flex: 1,
          }}
        >
          {finalSections.slice(1, finalSections.length).map((section) => (
            <Box
              key={section.name}
              data-testid={`sidebar-section-${section.name}`}
              sx={{ px: '4px' }}
            >
              <NavSection
                section={section}
                accessiblePages={accessiblePages}
                handleDrawerClick={handleDrawerClick}
                closed={closed && !mobileOpen}
                openedMenu={openedMenu === section.name}
                setMobileOpen={setMobileOpen}
              />
            </Box>
          ))}
        </Box>

        <Box
          data-testid="sidebar-bottom-sections"
          display="flex"
          justifyContent="space-between"
          alignItems="end"
          flexDirection="column"
          sx={{
            paddingX: closed ? '4px' : '0px',
            paddingTop: '4px',
            width: '100%',
          }}
        >
          {<LanguageSelector closed={closed} />}
          <Box width="100%">
            {bottomSections.map((section) => (
              <Box
                key={section.name}
                data-testid={`sidebar-bottom-section-${section.name}`}
                overflow="visible"
              >
                <NavSection
                  section={section}
                  accessiblePages={accessiblePages}
                  handleDrawerClick={handleDrawerClick}
                  closed={closed && !mobileOpen}
                  openedMenu={openedMenu === section.name}
                  customButtonStyles={{ padding: '6px 16px' }}
                  setMobileOpen={setMobileOpen}
                />
              </Box>
            ))}
          </Box>

          {fastApiToggleAvailable && !closed && (
            <Box
              data-testid="sidebar-fast-api-env-toggle"
              width="100%"
              display="flex"
              justifyContent="start"
              alignItems="center"
              pl={1.5}
              pr={1.5}
              sx={{ height: '44px' }}
            >
              <Box
                display="flex"
                justifyContent="start"
                alignItems="center"
                sx={{ minWidth: 28 }}
              >
                <Icon icon={SwapHoriz} sx={{ p: '2px' }} size="medium" />
              </Box>
              <Switch
                labelStyles={{
                  margin: 0,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
                label="New Mira Arch"
                checked={fastApiEnv === 'new'}
                onChange={toggleFastApiEnv}
              />
            </Box>
          )}
          {fastApiToggleAvailable && closed && (
            <ThemedTooltip
              title={fastApiEnv === 'new' ? 'New Mira Arch: on' : 'New Mira Arch: off'}
              placement="right"
              data-testid="section-tooltip"
            >
              <Box
                data-testid="sidebar-fast-api-env-toggle"
                width="100%"
                display="flex"
                justifyContent="center"
                alignItems="center"
              >
                <ListItemButton
                  onClick={toggleFastApiEnv}
                  sx={{ padding: '10px 10px', borderRadius: '10px' }}
                >
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                  >
                    <Icon icon={SwapHoriz} sx={{ p: '2px' }} size="medium" />
                  </Box>
                </ListItemButton>
              </Box>
            </ThemedTooltip>
          )}
          {!closed && (
            <Box
              data-testid="sidebar-dark-mode-toggle"
              width="100%"
              display="flex"
              justifyContent="start"
              alignItems="center"
              pl={1.5}
              pr={1.5}
              sx={{ height: '44px' }}
            >
              <Box
                display="flex"
                justifyContent="start"
                alignItems="center"
                sx={{ minWidth: 28 }}
              >
                <Icon icon={LightMode} sx={{ p: '2px' }} size="medium" />
              </Box>
              <Switch
                labelStyles={{
                  margin: 0,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
                label={t('menu.darkMode')}
                checked={theme.palette.mode === 'dark'}
                onChange={toggleMode}
              />
            </Box>
          )}
          {closed && (
            <ThemedTooltip
              title={t('menu.darkMode')}
              placement="right"
              data-testid="section-tooltip"
            >
              <Box
                data-testid="sidebar-dark-mode-toggle"
                width="100%"
                display="flex"
                justifyContent="center"
                alignItems="center"
              >
                <ListItemButton
                  sx={{
                    padding: '10px 10px',
                    borderRadius: '10px',
                  }}
                >
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                  >
                    <Icon icon={LightMode} sx={{ p: '2px' }} size="medium" />
                  </Box>
                </ListItemButton>
              </Box>
            </ThemedTooltip>
          )}
        </Box>
      </Box>
      <AuthButton closed={closed} />
    </Box>
  );

  return (
    <Box
      data-testid="sidebar-container"
      ref={sidebarRef}
      sx={{
        height: '100%',
        ':hover': {},
      }}
    >
      {/* The implementation can be swapped with js to avoid SEO duplication of links. */}
      <Drawer
        anchor="right"
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerClose}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: {
            xs: 'block',
            sm: 'block',
            md: 'block',
            lg: 'none',
          },
          overflow: 'visible',
          '& .MuiDrawer-paper': {
            width:
              closed && !mobileOpen
                ? '48px'
                : {
                    width: '100%',
                    '@media (min-width: 961px)': {
                      width: '320px',
                    },
                  },
            backgroundColor: theme.palette.royal10.main,
            transition: transition,
            overflow: 'visible',
          },
        }}
      >
        {drawer}
      </Drawer>
      <Drawer
        variant="permanent"
        sx={{
          display: {
            xs: 'none',
            sm: 'none',
            md: 'none',
            lg: 'block',
          },
          overflow: 'visible',
          '& .MuiDrawer-paper': {
            width: closed && !mobileOpen ? '52px' : '320px',
            backgroundColor: theme.palette.royal10.main,
            transition: transition,
            overflow: 'visible',
            height: '100%',
          },
        }}
        open
      >
        {drawer}
      </Drawer>
    </Box>
  );
};

export default Sidebar;
